import React, { FC, useState } from "react";
import { Link } from "react-router-dom";
import SaleOffBadge from "modules/ui/components/SaleOffBadge/SaleOffBadge";
import Badge from "modules/ui/shared/Badge/Badge";
import HotelGallery from "./HotelGallery";
import HotelRedirectModal from "./HotelRedirectModal";
import HotelDetail from "../../../modules/hotels/types/HotelDetail";
import HotelImage from "../../../modules/hotels/types/HotelImage";
import {StarIcon} from "@heroicons/react/24/solid";
import {BookingCom} from "../../../modules/hotels/components/icons/BookingCom";
import useTranslate from "../../../modules/core/languages/hooks/useTranslate";
import {formatNumber} from "../../../modules/common/utils/number";
import useCustomerContext from "../../../modules/customers/hooks/useCustomerContext";
import useExternalHotelUrl from "../../../modules/hotels/hooks/useExternalHotelUrl";
import {Trans} from "react-i18next";

export interface HotelGridCardProps {
    hotelDetail: HotelDetail;
    cityEventId: number;
    key?: string;
}

const HotelGridCard: FC<HotelGridCardProps> = ({
    hotelDetail,
    cityEventId
}) => {
    const t = useTranslate();
    const { languageCode } = useCustomerContext();
    const [showRedirectModal, setShowRedirectModal] = useState(false);

    const { url: onClickUrl, isExternal: redirect } = useExternalHotelUrl({
        externalUrl: hotelDetail.externalUrl,
        internalUrl: `/event-city/${cityEventId}/${hotelDetail.id}/hotel/${hotelDetail.hotelObject.id}`
    });

    const showDiscountCode = redirect && null !== hotelDetail.discountCode;

    const renderSliderGallery = (hotel:HotelDetail) => {
        return (
            <div className="relative flex-shrink-0 w-full md:w-72 ">
                <HotelGallery
                    ratioClass="aspect-w-6 aspect-h-5"
                    galleryImgs={
                        hotelDetail.images.map((image:HotelImage) => (image.contentUrl))
                    }
                    uniqueID={`StayCardH_${hotel.hotelObject.id}`}
                    href={onClickUrl}
                    redirect={redirect}
                    linkActive={hotelDetail.availableBooking}
                />
                {hotel.trHighDemand[languageCode] && <SaleOffBadge className="absolute left-3 top-3" />}
            </div>
        );
    };

    const renderSuggestedRoom = (hotel:HotelDetail) => {
        return (
            <>
                <div>
                    {hotel.suggestedRoom?.trType[languageCode]}
                </div>
                <div>
                   {[...Array(hotel.suggestedRoom?.adults || 0)].map((value, index) =>
                       <i className="las la-user la-2x" key={index}></i>
                   )}
                    {[...Array(hotel.suggestedRoom?.children || 0)].map((value, index) =>
                        <i className="text-2xl las la-user" key={index}></i>
                    )}
                </div>
            </>
        );
    };

    const minimumNightsRequires = (hotel:HotelDetail) => {
        return (
            <>
                <Badge name={
                    t("hotelMapCard.minNightsRequired", {"noches" : hotelDetail.minimalNights})
                } color="red"/>
            </>
        );
    };

    const renderDiscountCode = () => {
        if (showDiscountCode) {
            return (
                <>
                    <Badge name={
                        t("hotelMapCard.discountCode", {"code" : hotelDetail.discountCode})
                    } color="red"/>
                </>
            );
        } else {
            return "";
        }
    };

    const renderContent = (hotel:HotelDetail) => {
        let priceAvg = hotel.suggestedRoom?.minPrice ?? 0;

        return (
            <div className="flex-grow p-3 sm:p-5 flex flex-col">
                <div className="space-y-2">
                    <div className="flex items-end">
                        {[...Array(hotel.hotelObject.stars)].map((value, index) => <StarIcon className="w-[18px] h-[18px] text-orange-500" key={index} />)}
                        <div className="flex items-end" style={{marginLeft:'auto' }}>
                            {renderDiscountCode()}
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">
                        <h2 className="text-lg font-medium capitalize">
                            <span className="line-clamp-1">{hotel.hotelObject.name}</span>
                        </h2>
                    </div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400"><i className="las la-map-marker text-lg ml"></i> {hotel.hotelObject.address}</p>
                </div>
                <div className="hidden sm:block w-14 border-b border-neutral-100 dark:border-neutral-800 my-4"></div>
                {hotelDetail.availableBooking ?
                    renderSuggestedRoom(hotel)
                    :
                    minimumNightsRequires(hotel)
                }
                <div className="w-14 border-b border-neutral-100 dark:border-neutral-800 my-4"></div>
                <div className="flex justify-between items-end">
                    {hotel.trDistanceToTheEvent[languageCode] && (
                    <Badge name={hotel.trDistanceToTheEvent[languageCode]} color="green" />
                    )}
                    {hotel.suggestedRoom && hotelDetail.availableBooking && (
                        <span className="text-base font-semibold text-secondary-500">
                            {/*{hotelDetail.pricePrefix && (*/}
                            {/*    <span className="text-sm text-neutral-500 dark:text-neutral-400 font-normal">*/}
                            {/*        {hotelDetail.pricePrefix}&nbsp;*/}
                            {/*    </span>*/}
                            {/*)}*/}
                            {/*{formatNumber(priceAvg)} {' '}*/}
                            {/*<span dangerouslySetInnerHTML={{ __html:hotel.suggestedRoom.currency}} />*/}
                            <span className="text-sm text-neutral-500 dark:text-neutral-400 font-normal">
                                <Trans
                                    i18nKey="hotelMapCard.forNight"
                                    components={{
                                        PriceFormatter: <span className="text-base font-semibold text-secondary-500"/>,
                                        price: formatNumber(priceAvg) + ' ' + hotel.suggestedRoom.currency
                                    }}
                                />
                            </span>
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const renderOnClickUrl = () => {
        if (!redirect) {
            return <Link to={onClickUrl} className="absolute inset-0"></Link>;
        } else {
            return (
                <div 
                    onClick={() => setShowRedirectModal(true)}
                    className="absolute inset-0 cursor-pointer"
                />
            );
        }
    };

    const handleConfirmRedirect = () => {
        window.open(onClickUrl, '_blank', 'noopener,noreferrer');
        setShowRedirectModal(false);
    };

    return (
        <>
            <div
                className={`nc-StayCardH group relative bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow will-change-transform`}
                data-nc-id="StayCardH"
            >
                {hotelDetail.availableBooking && (
                    renderOnClickUrl()
                )}

                {redirect && (
                    <div 
                        className="absolute top-3 right-3 z-10 bg-white dark:bg-neutral-800 rounded-lg p-2 cursor-pointer"
                        onClick={() => setShowRedirectModal(true)}
                    >
                        <BookingCom size={100} />
                    </div>
                )}

                {hotelDetail ?
                <div className="grid grid-cols-1 md:flex md:flex-row ">
                    {renderSliderGallery(hotelDetail)}
                    {renderContent(hotelDetail)}
                </div>
                : null }
            </div>
            <HotelRedirectModal 
                isOpen={showRedirectModal}
                onClose={() => setShowRedirectModal(false)}
                onConfirm={handleConfirmRedirect}
            />
        </>
    );
};

export default HotelGridCard;
